import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
    Loader2, 
    MessageSquare,
    CheckCircle,
    Eye,
    XCircle,
    Clock,
    ChevronDown,
    ChevronUp
} from 'lucide-react';
import { adminAPI } from '../../lib/api';
import { formatDate, cn } from '../../lib/utils';
import { toast } from 'sonner';

const AdminFeedback = () => {
    const [loading, setLoading] = useState(true);
    const [feedback, setFeedback] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [statusFilter, setStatusFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        loadFeedback();
    }, [page, statusFilter]);

    const loadFeedback = async () => {
        try {
            const response = await adminAPI.getFeedback(page, 50, statusFilter);
            setFeedback(response.data.feedback);
            setTotal(response.data.total);
        } catch (err) {
            console.error('Failed to load feedback:', err);
            toast.error('Failed to load feedback');
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (feedbackId, newStatus) => {
        try {
            await adminAPI.updateFeedbackStatus(feedbackId, newStatus);
            toast.success(`Feedback marked as ${newStatus}`);
            loadFeedback();
        } catch (err) {
            console.error('Failed to update status:', err);
            toast.error('Failed to update status');
        }
    };

    const getStatusBadge = (status) => {
        const styles = {
            new: 'bg-blue-500',
            reviewed: 'bg-amber-500',
            resolved: 'bg-emerald-500',
            dismissed: 'bg-gray-500'
        };
        return <Badge className={styles[status] || 'bg-gray-500'}>{status}</Badge>;
    };

    const statusCounts = {
        all: total,
        new: feedback.filter(f => f.status === 'new').length,
        reviewed: feedback.filter(f => f.status === 'reviewed').length,
        resolved: feedback.filter(f => f.status === 'resolved').length
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-bold text-foreground">User Feedback</h1>
                    <p className="text-muted-foreground">Review issues and feature suggestions from users</p>
                </div>

                {/* Filter tabs */}
                <div className="flex gap-2 flex-wrap">
                    {['all', 'new', 'reviewed', 'resolved', 'dismissed'].map((status) => (
                        <Button
                            key={status}
                            variant={statusFilter === status ? 'default' : 'outline'}
                            size="sm"
                            onClick={() => {
                                setStatusFilter(status);
                                setPage(1);
                            }}
                        >
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </Button>
                    ))}
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-foreground">{total}</div>
                            <p className="text-sm text-muted-foreground">Total Feedback</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-blue-500">{statusCounts.new}</div>
                            <p className="text-sm text-muted-foreground">New</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-amber-500">{statusCounts.reviewed}</div>
                            <p className="text-sm text-muted-foreground">Under Review</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="text-2xl font-bold text-emerald-500">{statusCounts.resolved}</div>
                            <p className="text-sm text-muted-foreground">Resolved</p>
                        </CardContent>
                    </Card>
                </div>

                {/* Feedback list */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <MessageSquare className="h-5 w-5" />
                            Feedback ({total})
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {feedback.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                                No feedback found
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {feedback.map((item) => (
                                    <div 
                                        key={item.id}
                                        className="border border-border rounded-lg overflow-hidden"
                                    >
                                        <div 
                                            className="p-4 flex items-start justify-between cursor-pointer hover:bg-accent/30"
                                            onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                                        >
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 mb-1">
                                                    {getStatusBadge(item.status)}
                                                    <span className="text-sm text-muted-foreground">
                                                        {formatDate(item.created_at)}
                                                    </span>
                                                </div>
                                                <p className="text-foreground font-medium truncate">
                                                    {item.message.substring(0, 100)}
                                                    {item.message.length > 100 && '...'}
                                                </p>
                                                <p className="text-xs text-muted-foreground mt-1">
                                                    From: {item.user_email} • Page: {item.page}
                                                </p>
                                            </div>
                                            <div className="ml-4">
                                                {expandedId === item.id ? (
                                                    <ChevronUp className="h-4 w-4 text-muted-foreground" />
                                                ) : (
                                                    <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </div>
                                        </div>
                                        
                                        {expandedId === item.id && (
                                            <div className="p-4 bg-accent/20 border-t border-border">
                                                <div className="mb-4">
                                                    <h4 className="text-sm font-semibold text-foreground mb-2">Full Message:</h4>
                                                    <p className="text-muted-foreground whitespace-pre-wrap bg-accent p-3 rounded-lg">
                                                        {item.message}
                                                    </p>
                                                </div>
                                                
                                                <div className="flex gap-2 flex-wrap">
                                                    {item.status !== 'reviewed' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => updateStatus(item.id, 'reviewed')}
                                                        >
                                                            <Eye className="h-4 w-4 mr-1" />
                                                            Mark Reviewed
                                                        </Button>
                                                    )}
                                                    {item.status !== 'resolved' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => updateStatus(item.id, 'resolved')}
                                                            className="text-emerald-600"
                                                        >
                                                            <CheckCircle className="h-4 w-4 mr-1" />
                                                            Mark Resolved
                                                        </Button>
                                                    )}
                                                    {item.status !== 'dismissed' && (
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => updateStatus(item.id, 'dismissed')}
                                                            className="text-muted-foreground"
                                                        >
                                                            <XCircle className="h-4 w-4 mr-1" />
                                                            Dismiss
                                                        </Button>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};

export default AdminFeedback;
